export const RETURN_TO_BASE_URL = 'https://return-to.local';

export type ReturnToMatcher = (pathname: string, searchParams: URLSearchParams) => boolean;
export type NavigationTargetKey = 'listTo' | 'returnTo';

export const sanitizeReturnToPath = (returnTo: string | null): string | null => {
  if (!returnTo) {
    return null;
  }

  const normalizedReturnTo = returnTo.replace(/\\/g, '/');

  if (!normalizedReturnTo.startsWith('/') || normalizedReturnTo.startsWith('//')) {
    return null;
  }

  return normalizedReturnTo;
};

const toRelativeUrl = (url: URL) => `${url.pathname}${url.search}${url.hash}`;

export const parseNavigationPath = (path: string | null): URL | null => {
  const sanitizedPath = sanitizeReturnToPath(path);

  if (!sanitizedPath) {
    return null;
  }

  return new URL(sanitizedPath, RETURN_TO_BASE_URL);
};

export const stripNavigationTargets = (path: string): string => {
  const url = parseNavigationPath(path);

  if (!url) {
    return '/';
  }

  url.searchParams.delete('listTo');
  url.searchParams.delete('returnTo');

  return toRelativeUrl(url);
};

export const getNavigationTarget = (searchParams: URLSearchParams, key: NavigationTargetKey): string | null => {
  const target = sanitizeReturnToPath(searchParams.get(key));

  if (!target) {
    return null;
  }

  return key === 'listTo' ? stripNavigationTargets(target) : target;
};

export const buildNavigationSearchParams = ({
  listTo,
  returnTo,
}: {
  listTo?: string | null;
  returnTo?: string | null;
}) => {
  const searchParams = new URLSearchParams();
  const sanitizedReturnTo = sanitizeReturnToPath(returnTo ?? null);
  const sanitizedListTo = sanitizeReturnToPath(listTo ?? null);

  if (sanitizedReturnTo) {
    searchParams.set('returnTo', sanitizedReturnTo);
  }

  if (sanitizedListTo) {
    searchParams.set('listTo', stripNavigationTargets(sanitizedListTo));
  }

  return searchParams;
};

export const getCurrentNavigationPath = (
  location?: {
    hash?: string;
    pathname: string;
    search: string;
  } | null
) => {
  // Nuqs can update the browser URL before React Router's location object catches up,
  // so the live window location is the source of truth for persisted navigation targets.
  if (typeof window !== 'undefined') {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  if (location) {
    return `${location.pathname}${location.search}${location.hash ?? ''}`;
  }

  return '/';
};

const parseReturnToPath = (path: string | null) => {
  const url = parseNavigationPath(path);

  if (!url) {
    return null;
  }

  return {
    pathname: url.pathname,
    searchParams: url.searchParams,
  };
};

export const getReturnToChain = (returnTo: string | null): string[] => {
  const chain: string[] = [];
  const visitedPaths = new Set<string>();
  let currentPath = sanitizeReturnToPath(returnTo);

  while (currentPath && !visitedPaths.has(currentPath)) {
    chain.push(currentPath);
    visitedPaths.add(currentPath);

    const parsedPath = parseReturnToPath(currentPath);
    currentPath = sanitizeReturnToPath(parsedPath?.searchParams.get('returnTo') ?? null);
  }

  return chain;
};

export const findReturnToMatch = (chain: string[], matcher: ReturnToMatcher): string | null => {
  for (const path of chain) {
    const parsedPath = parseReturnToPath(path);

    if (!parsedPath) {
      continue;
    }

    if (matcher(parsedPath.pathname, parsedPath.searchParams)) {
      return path;
    }
  }

  return null;
};
